import React, { useEffect, useState } from 'react';
import { ChevronRight, Loader2 } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Input from '../ui/Input';
import {
  clearDomainState,
  selectDomainsLoading,
  selectDomainSuccess,
} from 'store/domain';
import { useSelector } from 'react-redux';
import { useToast } from 'contexts/ToastContext';
import { useDispatch } from 'react-redux';
import { cn } from '../../utils/cn';

const awsSesLogo = require('../../assets/aws-ses.png');
const mailgunLogo = require('../../assets/mailgun-logo.png');

interface FormData {
  domain: string;
  displayName: string;
  emailAddress: string;
  provider?: 'aws' | 'mailgun';
}

interface AddDomainModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: FormData) => void;
}

type EmailProvider = 'aws-ses' | 'mailgun' | null;

const AddDomainModal: React.FC<AddDomainModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const success = useSelector(selectDomainSuccess) as boolean;
  const loading = useSelector(selectDomainsLoading) as boolean;
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const [selectedProvider, setSelectedProvider] =
    useState<EmailProvider>('mailgun');
  const [formData, setFormData] = useState<FormData>({
    domain: '',
    displayName: '',
    emailAddress: '',
  });

  const [errors, setErrors] = useState<Partial<FormData>>({});

  useEffect(() => {
    if (success) {
      showToast({
        type: 'success',
        title: 'Success',
        message: 'Domain added successfully',
      });
      setFormData({
        domain: '',
        displayName: '',
        emailAddress: '',
      });

      setErrors({});
      setSelectedProvider(null);
      onClose();
      dispatch(clearDomainState());
    }
  }, [success, showToast, onClose, dispatch]);

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};

    if (!formData.domain.trim()) {
      newErrors.domain = 'Domain is required';
    }

    if (!formData.displayName.trim()) {
      newErrors.displayName = 'Sender Name is required';
    }

    if (!formData.emailAddress.trim()) {
      newErrors.emailAddress = 'Email address is required';
    } else if (!/^[^\s@]+$/.test(formData.emailAddress)) {
      newErrors.emailAddress =
        'Please enter a valid email address (without @ symbol)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Combine email address with domain
    const fullEmailAddress = `${formData.emailAddress}@${formData.domain}`;

    // Map provider selection to the correct format
    const provider = selectedProvider === 'aws-ses' ? 'aws' : 'mailgun';

    const submitData: FormData = {
      ...formData,
      emailAddress: fullEmailAddress,
      provider,
    };

    onSubmit(submitData);
  };

  const handleCancel = () => {
    // Reset form
    setFormData({
      domain: '',
      displayName: '',
      emailAddress: '',
    });
    setErrors({});
    setSelectedProvider('mailgun');
    onClose();
  };

  const handleProviderSelect = (provider: EmailProvider) => {
    setSelectedProvider(provider);
  };

  // const handleBackToProviderSelection = () => {
  //   setSelectedProvider('mailgun');
  // };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title={selectedProvider ? 'Add sending domain' : 'Choose provider'}
      size="small"
    >
      {!selectedProvider ? (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground text-center">
            Choose your email service provider to add a sending domain
          </p>

          <div className="space-y-2">
            <button
              type="button"
              className="w-full relative p-3 border border-border rounded-lg bg-card text-left hover:border-primary hover:bg-muted/50 transition-all duration-200 group focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background"
              onClick={() => handleProviderSelect('aws-ses')}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg border border-border bg-background flex items-center justify-center shrink-0">
                  <img
                    src={awsSesLogo}
                    alt=""
                    className="w-6 h-6 object-contain"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-foreground">
                    AWS SES
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Amazon Simple Email Service
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary shrink-0 transition-colors" />
              </div>
            </button>

            <button
              type="button"
              className="w-full relative p-3 border border-border rounded-lg bg-card text-left hover:border-primary hover:bg-muted/50 transition-all duration-200 group focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background"
              onClick={() => handleProviderSelect('mailgun')}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg border border-border bg-background flex items-center justify-center shrink-0">
                  <img
                    src={mailgunLogo}
                    alt=""
                    className="w-6 h-6 object-contain"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-foreground">
                    Mailgun
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Email API for developers
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary shrink-0 transition-colors" />
              </div>
            </button>
          </div>

          <div className="flex justify-end pt-2 border-t border-border">
            <Button
              type="button"
              variant="outline"
              size="small"
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border border-border">
            <div className="flex items-center gap-2">
              <img
                src={selectedProvider === 'aws-ses' ? awsSesLogo : mailgunLogo}
                alt=""
                className="w-6 h-6 object-contain"
              />
              <span className="text-sm font-medium text-foreground">
                {selectedProvider === 'aws-ses' ? 'AWS SES' : 'Mailgun'}
              </span>
            </div>
            {/* <button
              type="button"
              onClick={handleBackToProviderSelection}
              className="text-xs text-primary hover:text-primary-hover font-medium focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
            >
              Change
            </button> */}
          </div>

          <Input
            label="Domain"
            placeholder="example.com"
            value={formData.domain}
            onChange={(e) => handleInputChange('domain', e.target.value)}
            error={errors.domain}
            required
            disabled={loading}
          />

          <Input
            label="Sender name"
            placeholder="Your Name"
            value={formData.displayName}
            onChange={(e) => handleInputChange('displayName', e.target.value)}
            error={errors.displayName}
            required
            disabled={loading}
          />

          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">
              Sender address <span className="text-destructive">*</span>
            </label>
            <div className="flex rounded-lg overflow-hidden border border-border focus-within:ring-1 focus-within:ring-primary focus-within:border-primary transition-colors">
              <input
                type="text"
                className={cn(
                  'flex-1 px-3 py-2 text-sm bg-background text-foreground placeholder-muted-foreground focus:outline-none min-w-0',
                  errors.emailAddress && 'border-destructive'
                )}
                placeholder="info"
                value={formData.emailAddress}
                onChange={(e) =>
                  handleInputChange('emailAddress', e.target.value)
                }
                required
                disabled={loading}
              />
              <div className="px-3 py-2 bg-muted border-l border-border text-muted-foreground text-sm flex items-center shrink-0">
                @{formData.domain || 'example.com'}
              </div>
            </div>
            {errors.emailAddress && (
              <p className="text-sm text-destructive">{errors.emailAddress}</p>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              size="small"
              disabled={loading}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="small"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Adding…
                </>
              ) : (
                'Add domain'
              )}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default AddDomainModal;
